import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

// Admin: Test-/Fehl-Unterschrift eines Angebots aufheben (mit Pflicht-Grund).
export default function RevokeSignatureButton({ offer, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const sig = offer.signature || {};
  const when = sig.signed_at_display || (sig.signed_at ? new Date(sig.signed_at).toLocaleDateString("de-DE") : "");

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/revoke-signature`, { reason: reason.trim() });
      toast.success(`Unterschrift von ${offer.offer_number} aufgehoben`);
      setOpen(false);
      setReason("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aufheben fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`offer-revoke-signature-${offer.id}`}
        className="h-8 px-3 rounded-lg border border-red-500/40 text-red-300 text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-red-500/10 active:scale-95 transition-transform">
        <Undo2 size={13} /> Unterschrift aufheben
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-md bg-[#18181B] border-zinc-800 text-zinc-100" data-testid="revoke-signature-dialog">
          <DialogTitle className="text-[16px] font-bold">Unterschrift aufheben?</DialogTitle>
          <DialogDescription className="text-[12.5px] text-zinc-400 -mt-1">
            Angebot <b className="text-zinc-200">{offer.offer_number}</b> · unterschrieben von <b className="text-zinc-200">{sig.signer_name || "unbekannt"}</b>{when ? ` am ${when}` : ""}
          </DialogDescription>
          <ul className="text-[12px] text-zinc-300 space-y-1 list-disc pl-4">
            <li>Das Angebot gilt wieder als nicht unterschrieben, das PDF wird ohne Unterschrift neu erstellt.</li>
            <li>Das Projekt springt im Ablauf vor „Auftrag unterschrieben“ zurück und verschwindet aus den BzA-Spalten.</li>
            <li>Bereits versendete E-Mails (z. B. Auftragsbestätigung) werden nicht zurückgeholt.</li>
            <li>Die alte Unterschrift bleibt mit Ihrem Grund im Verlauf nachvollziehbar.</li>
          </ul>
          <div>
            <label className="block text-[12px] mb-1">Grund (Pflicht)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="revoke-signature-reason"
              placeholder="z. B. Interne Test-Unterschrift, Kunde hat nicht angenommen"
              className="w-full rounded-lg px-3 py-2 text-[13px]" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(false)} disabled={busy} data-testid="revoke-signature-cancel"
              className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-[13px] font-semibold hover:bg-zinc-700">
              Abbrechen
            </button>
            <button type="button" onClick={submit} disabled={busy || reason.trim().length < 5} data-testid="revoke-signature-confirm"
              className="flex-1 h-10 rounded-lg bg-red-500 text-white font-bold text-[13px] inline-flex items-center justify-center gap-2 hover:bg-red-600 disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />} Unterschrift aufheben
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Hinweis am Angebot, wenn früher eine Unterschrift aufgehoben wurde.
export function RevokedSignatureNote({ offer }) {
  const last = (offer.revoked_signatures || []).slice(-1)[0];
  if (!last || offer.signature?.signed_at) return null;
  return (
    <div className="mt-1 text-[11.5px] text-red-300/90" data-testid={`offer-${offer.id}-revoked-note`}>
      Unterschrift von {last.signature?.signer_name || "unbekannt"} aufgehoben am {new Date(last.revoked_at).toLocaleDateString("de-DE")}
      {last.revoked_by_name ? ` durch ${last.revoked_by_name}` : ""} · {last.reason}
    </div>
  );
}
